import moment from 'moment';
import { REVERSE_DASH_DATETIME_FORMAT } from '../common/utils/date/Date.utils';

export const transformBooleanToYesNo = (value) => {
  const resolvedValue = value?.value ?? value;
  if (resolvedValue === true) return 'YES';
  if (resolvedValue === false) return 'NO';
  return null;
};

export const transformYesNoToBoolean = (value, yesLabel = 'Yes', noLabel = 'No') => {
  if (value === 'YES') return { value: true, label: yesLabel };
  if (value === 'NO') return { value: false, label: noLabel };
  return null;
};

export const transformKeysToEnum = (keys) => {
  const keyValue = keys?.value || keys;
  if (keyValue === '1') return 'ONE';
  if (keyValue === '2') return 'TWO';
  if (keyValue === '3') return 'TWO_PLUS';
  return null;
};

export const convertToPence = (value) => {
  if (!value) return null;
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isNaN(numValue) ? null : Math.round(numValue * 100);
};

export const getValueOrDefault = (value, defaultValue) =>
  value !== null && value !== undefined ? value : defaultValue;

export const extractSelectValue = (selectObj) => selectObj?.value || selectObj;

export const extractSelectLabel = (selectObj) => selectObj?.label || selectObj;

export const transformBasicDataToAPI = (values) => {
  if (!values) return null;
  const hasRegPlate = values.registrationPlate?.trim();
  const hasMakeId = extractSelectValue(values.makeSelect) || values.makeId;
  const hasModelId = extractSelectValue(values.modelSelect) || values.modelId;
  const hasVin = values.vin?.trim();

  if (!hasRegPlate && !hasMakeId && !hasModelId && !hasVin) {
    return null;
  }

  return {
    // Transform makeSelect object to makeId integer
    makeId: extractSelectValue(values.makeSelect) || values.makeId || null,
    makeName: extractSelectLabel(values.makeSelect) || values.makeName || null,

    // Transform modelSelect object to modelId integer
    modelId: extractSelectValue(values.modelSelect) || values.modelId || null,
    modelName: extractSelectLabel(values.modelSelect) || values.modelName || null,

    // Keep string fields as is
    derivative: values.derivative || null,
    registrationPlate: values.registrationPlate?.toUpperCase() || null,
    carType: extractSelectValue(values.carType) || null,
    colour: extractSelectValue(values.colour) || null,
    vin: values.vin || null,
    dateOfFirstRegistration: (() => {
      const input = values.dateOfFirstRegistration;
      if (!input) return null;
      const parsed = moment(input, 'DD/MM/YYYY', true);
      return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
    })(),
    mot: values.mot || null,
    vehicleCapCapId: values.vehicleCapCapId ? parseInt(values.vehicleCapCapId, 10) : null,

    // Convert string numbers to integers
    mileage: values.mileage ? parseInt(values.mileage, 10) : null,
    year: values.year ? parseInt(values.year, 10) : null,
    doors: values.doors ? parseInt(values.doors, 10) : null,
    engineSize: values.engineSize ? parseInt(values.engineSize, 10) : null,
    previousKeepersInTotal: values.previousKeepersInTotal
      ? parseInt(values.previousKeepersInTotal, 10)
      : null,

    // Transform select objects to their string values
    fuel: extractSelectValue(values.fuel) || null,
    transmission: extractSelectValue(values.transmission) || null,

    // Transform boolean select objects to YES/NO strings
    ulezCompliant: transformBooleanToYesNo(values.ulezCompliant) || null,
    v5cLogbook: transformBooleanToYesNo(values.v5cLogBook) || null,

    // Transform keys select to numbersOfKeyIncluded enum
    numbersOfKeyIncluded: transformKeysToEnum(values.keys) || null,

    vehicleType: extractSelectValue(values?.vehicleType)?.toUpperCase() || null,
    vatQualifying: values?.vatQualifying?.value,
  };
};

export const transformVehicleFeaturesToAPI = (values) => {
  if (!values) return null;

  const prepareVehicleFeatures = () => {
    if (values.vehicleFeaturesDictionary?.length) {
      return values.vehicleFeaturesDictionary.map((item) => {
        if (typeof item === 'object') {
          return item.id;
        }
        return item;
      });
    }
    return [];
  };

  return {
    vehicleFeaturesDictionaryIds: prepareVehicleFeatures() || [],
    vehicleFeaturesCustom: values.vehicleFeaturesCustom?.length
      ? values.vehicleFeaturesCustom
      : [],
  };
};

export const convertToNumber = (value) => {
  if (!value) return null;
  const numValue = extractSelectValue(value);
  if (typeof numValue === 'number') return numValue;
  if (typeof numValue === 'string') {
    const parsed = parseInt(numValue.replace(/[^\d]/g, ''), 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

export const transformTyreThreadDepthsToAPI = (values) => {
  if (!values) return null;

  const extractNumericLabel = (selectObj) => {
    if (selectObj && typeof selectObj.label === 'number') {
      return selectObj.label;
    }
    return null;
  };

  const nearsideFront = extractNumericLabel(values?.nearsideFront);
  const nearsideRear = extractNumericLabel(values?.nearsideRear);
  const offsideFront = extractNumericLabel(values?.offsideFront);
  const offsideRear = extractNumericLabel(values?.offsideRear);

  if (
    nearsideFront === null &&
    nearsideRear === null &&
    offsideFront === null &&
    offsideRear === null
  ) {
    return null;
  }

  return {
    nearsideFront,
    nearsideRear,
    offsideFront,
    offsideRear,
  };
};

export const transformVehicleMediaToAPI = (values) => {
  if (!values) return null;

  const transformPhoto = (photo) => {
    if (!photo) {
      return null;
    }

    const basePhoto = {
      fileUrl: photo.fileUrl || '',
      perspective: photo.perspective || '',
      sortNumber: photo.sortNumber || 0,
      mediaMetadataId: photo.mediaMetadataId || photo.id,
      thumbnailUrl: photo.thumbnailUrl || '',
    };

    if (photo.mediaMetadataId) {
      return {
        ...basePhoto,
        mediaMetadataId: photo.mediaMetadataId,
      };
    }

    if (photo.file) {
      return basePhoto;
    }
    return null;
  };

  const transformVideo = (video) => {
    if (!video || !video.mediaMetadataId) return null;

    return {
      fileUrl: video.fileUrl || '',
      mediaMetadataId: video.mediaMetadataId || '',
      previewUrl: video.previewUrl || '',
      previewMediaMetadataId: video.previewMediaMetadataId || '',
    };
  };

  return {
    photos: values.photos?.map(transformPhoto).filter(Boolean) || [],
    photosToDelete: values.photosToDelete || [],
    videos: values.videos?.map(transformVideo).filter(Boolean) || [],
    videosToDelete: values.videosToDelete || [],
  };
};

export const transformServiceHistoryToAPI = (values) => {
  if (!values) return null;

  const transformManualRecords = (records) => {
    if (!records?.length) return [];
    return records
      .filter((record) => record.date || record.mileage || record.serviceName)
      .map((record) => ({
        date: record.date || '',
        mileage: record.mileage ? parseInt(record.mileage, 10) : null,
        serviceName: record.serviceName || '',
        serviceType: record.serviceType.value || '',
      }));
  };

  const transformPhotoRecords = (records) => {
    if (!records?.length) return [];

    return records
      .filter((record) => record.mediaMetadataId)
      .map((record) => ({
        fileUrl: record.fileUrl || '',
        mediaMetadataId: record.mediaMetadataId || '',
      }));
  };

  const type = extractSelectValue(values.type) || values.type || '';
  const isNoneType = type === 'NONE';

  return {
    type,
    manualRecords: isNoneType ? [] : transformManualRecords(values.manualRecords) || [],
    photoRecords: isNoneType ? [] : transformPhotoRecords(values.photoRecords) || [],
    photoRecordsToDelete: values.photoRecordsToDelete || [],
  };
};

export const transformDescriptionAndSettingsToAPI = (values, organizationLocations = []) => {
  if (!values) return null;

  const defaultLocationId =
    organizationLocations.length > 0 ? organizationLocations[0].id : null;

  const getPublishTarget = () => 'EVERYWHERE';

  const getAuctionDates = () => {
    const formatForAPI = (dateString) => {
      if (!dateString || dateString === 'null') return null;
      const date = moment(dateString, REVERSE_DASH_DATETIME_FORMAT);
      return date.format('YYYY-MM-DDTHH:mm:ssZ');
    };
    if (values.startAuctionImmediately) {
      const endsOn = formatForAPI(values.auctionEndDate);
      return {
        startsOn: null,
        endsOn,
      };
    }

    const startsOn = formatForAPI(values.auctionStartDate);
    const endsOn = formatForAPI(values.auctionEndDate);

    return { startsOn, endsOn };
  };

  let typeValue;
  if (values.standardAuctionAndBuyNow && values.termsAccepted) {
    typeValue = extractSelectValue(values.type);
  } else if (values.termsAccepted) {
    typeValue = 'BUY_NOW_ONLY';
  } else {
    typeValue = extractSelectValue(values.type) || 'PHYSICAL';
  }
  const apiObject = {
    description: values.description?.trim() || undefined,
    defaultDescription: values.defaultDescription?.trim() || null,
    vehicleHighlightedInfo:
      (values.highlightedInfo || values.vehicleHighlightedInfo)?.trim() || null,
    locationId:
      getValueOrDefault(
        extractSelectValue(values.vehicleLocation) || values.locationId,
        defaultLocationId,
      ) || undefined,
    type: typeValue,
    publishTarget: getPublishTarget(),
    minimumPriceInPence: convertToPence(values.minimumPriceInPence) || null,
    autoAcceptPriceInPence: convertToPence(values.autoAcceptPriceInPence) || null,
    buyNowPriceInPence: convertToPence(values.buyNowPriceInPence) || null,
    ...getAuctionDates(),
    tryingToDealDeliveryDate: values.tryingToDealDeliveryDate || null,
    tryingToDealDeliveryDateType:
      extractSelectValue(values.tryingToDealDeliveryDateType) || null,
    isBuyNowFeatureEnable: !!values.termsAccepted,
  };

  // Remove undefined and null properties to avoid sending them in the payload
  Object.keys(apiObject).forEach((key) => {
    if (apiObject[key] === undefined || apiObject[key] === null) {
      delete apiObject[key];
    }
  });

  return apiObject;
};

export const transformDamagesToAPI = (values) => {
  if (!values?.length) return [];

  return values.map((damage) => ({
    fileUrl: damage.fileUrl || '',
    mediaMetadataId: damage.mediaMetadataId || '',
    damageType: damage.damageType || '',
  }));
};

export const transformVehicleDamagesToAPI = (vehicleDamagesData) => {
  if (!vehicleDamagesData) {
    return null;
  }

  const damagesArray = Array.isArray(vehicleDamagesData)
    ? vehicleDamagesData
    : vehicleDamagesData.damages;

  const damagesToDelete = Array.isArray(vehicleDamagesData)
    ? []
    : vehicleDamagesData.damagesToDelete || [];

  if ((!damagesArray || damagesArray.length === 0) && damagesToDelete.length === 0) {
    return null;
  }

  const transformDamagePhoto = (photo) => {
    if (!photo) return null;
    return {
      fileUrl: photo.fileUrl || '',
      mediaMetadataId: photo.mediaMetadataId || '',
    };
  };
  const transformDamage = (damage) => {
    if (!damage) return null;
    const { isPersisted, isEdit, section, ...rest } = damage;
    let base = {
      type: (rest.damageType || rest.type)?.toUpperCase(),
      description: rest?.description?.length > 0 ? rest.description : null,
      coordinateX: rest.x || rest.coordinateX,
      coordinateY: rest.y || rest.coordinateY,
      photos: (rest.photos || []).map(transformDamagePhoto).filter(Boolean),
      photosToDelete: rest.photosToDelete || [],
      section,
    };

    if (isEdit && rest.id) {
      base.id = rest.id;
    }

    if (isPersisted && !isEdit) {
      base = null;
    }

    return base;
  };

  return {
    damages: (damagesArray || []).map(transformDamage).filter(Boolean),
    damagesToDelete,
  };
};

export const transformAllFormDataToAPI = (formData, organizationLocations = []) => {
  const transformedDescriptionAndSettings =
    transformDescriptionAndSettingsToAPI(
      formData.descriptionAndSettings,
      organizationLocations,
    ) || null;
  const transformed = {
    id: formData.id || null,
    version: formData.version || 0,
    vehicleMedia: transformVehicleMediaToAPI(formData.vehicleMedia) || null,
    basicData: transformBasicDataToAPI(formData.basicData) || null,
    tyreThreadDepths: transformTyreThreadDepthsToAPI(formData.tyreThreadDepths) || null,
    serviceHistory: transformServiceHistoryToAPI(formData.serviceHistory) || null,
    vehicleFeatures: transformVehicleFeaturesToAPI(formData.vehicleFeatures) || null,
    descriptionAndSettings: transformedDescriptionAndSettings,
    vehicleDamages: transformVehicleDamagesToAPI(formData.vehicleDamages) || {
      damages: [],
      damagesToDelete: [],
    },
  };

  // eslint-disable-next-line no-unused-vars
  return Object.fromEntries(Object.entries(transformed).filter(([_, value]) => value != null));
};

export const deepMergeFormData = (stateData, formData) => ({
  ...stateData,
  ...formData,
  basicData: {
    ...stateData.basicData,
    ...formData.basicData,
  },
  vehicleFeatures: {
    ...stateData.vehicleFeatures,
    ...formData.vehicleFeatures,
  },
  descriptionAndSettings: {
    ...stateData.descriptionAndSettings,
    ...formData.descriptionAndSettings,
  },
  tyreThreadDepths: {
    ...stateData.tyreThreadDepths,
    ...formData.tyreThreadDepths,
  },
  serviceHistory: {
    ...stateData.serviceHistory,
    ...formData.serviceHistory,
  },
  vehicleDamages: {
    ...stateData.vehicleDamages,
    ...formData.vehicleDamages,
  },
  vehicleMedia: {
    ...stateData.vehicleMedia,
    ...formData.vehicleMedia,
  },
  damages: stateData.damages || [],
  damagesToDelete: stateData.damagesToDelete || [],
});
